// -------------------------------------
// Dependencies
// -------------------------------------
// Import Swiper library (Make sure you include Swiper in your project)
import Swiper, { Navigation, Scrollbar, A11y, Keyboard, Autoplay } from 'swiper';
import 'snippets/card/card';
import 'snippets/icon/icon';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/autoplay';
import './_testimonial.scss';

// -------------------------------------
// Variables
// -------------------------------------
const TESTIMONIALS_SELECTOR     = '.testimonials-carousel';
const CAROUSEL_WRAPPER_SELECTOR = '.testimonial-section__slider';
const CAROUSEL_SELECTOR         = '.carousel--testimonials';
const SWIPER_ARROW_SELECTOR     = '.swiper-arrow';
const PREV_BUTTON_SELECTOR      = '.swiper-button-prev';
const NEXT_BUTTON_SELECTOR      = '.swiper-button-next';
const SCROLL_BAR_SELECTOR       = '.swiper-scrollbar';

// Classes
const ARROW_INIT_CLASS = 'swiper-arrow-initialized';

// Other
const SLIDES_PER_VIEW_MOBILE  = 1.6;
const SLIDES_PER_VIEW_TABLET  = 2.6;
const SLIDES_PER_VIEW_LAPTOP  = 3.6;
const SLIDES_PER_VIEW_DESKTOP = 4.6;
const TABLET_MEDIA_QUERY      = '(min-width: 680px)';

// -------------------------------------
// Helper function to handle slides
// -------------------------------------
function handleTestimonialSlides(swiperEl) {
    swiperEl.slides.forEach(function (slide, index, array) {
        slide.addEventListener('focus', function (e) {
            var slidePos = swiperEl.getBoundingClientRect();

            // Remove standard scrolling to element in focus due to it going off the screen (slides left when it needs to stay on screen)
            swiperEl.scrollLeft = 0;
            swiperEl.slideTo(index);

            // Emulating normal tab focus due to needing to remove standard scroll
            // If the element is already in the viewport, do nothing
            // Otherwise, scroll to the element, and it should be in the middle of the screen when scrolling stops
            if (
                !(
                    slidePos.top >= 0 &&
                    slidePos.left >= 0 &&
                    slidePos.bottom <= window.innerHeight &&
                    slidePos.right <= window.innerWidth
                )
            ) {
                window.scrollTo({
                    top: slidePos.top + window.scrollY - window.innerHeight / 2,
                    left: 0,
                    behavior: 'smooth',
                });
            }
        });
    });
}

// Function to initialize Swiper sliders - Mobile
function initTestimonialsSwiperMobile (carousel) {
    const carouselWrapper = carousel.querySelector(CAROUSEL_WRAPPER_SELECTOR);

    if( carouselWrapper != null ) {
        // Destroy the previous Swiper instance, if it exists
        destroySwiper(carousel.swiper);

        const sectionID       = carousel.dataset.sectionId;
        const parentSelector  = TESTIMONIALS_SELECTOR + '--' + sectionID;
        const swiperArrows    = carouselWrapper.querySelectorAll(SWIPER_ARROW_SELECTOR);

        addClassToElements(swiperArrows, ARROW_INIT_CLASS);

        // Initialize the feature collection slider based on the unique classes generated above.
        const swiper = new Swiper(parentSelector + ' ' + CAROUSEL_SELECTOR, {
            // Optional parameters
            modules: [Navigation, Scrollbar, A11y, Keyboard],
            slidesPerView: SLIDES_PER_VIEW_MOBILE,
            spaceBetween: 15,
            centerInsufficientSlides: true,
            observer: true,
            observeParents: true,
            a11y: false,
            watchOverflow: true, // Do not turn into sliders if the number of slides is less than slidesPerView.
            grabCursor: true,
            freeMode: true,
            loop: false,
            autoplay: false,
            navigation: {
                nextEl: parentSelector + ' ' + NEXT_BUTTON_SELECTOR,
                prevEl: parentSelector + ' ' + PREV_BUTTON_SELECTOR
            },
            scrollbar: {
                el: parentSelector + ' ' + SCROLL_BAR_SELECTOR,
                draggable: true,
            }
        });

        if (swiper.slides) {
            handleTestimonialSlides(swiper);
        } else {
            swiper.forEach(function(swiperEl) {
                handleTestimonialSlides(swiperEl);
            })
        }

        // Save the created Swiper instance to the DOM element
        carousel.swiper = swiper;
    }
}

// Function to initialize Swiper sliders - Desktop
function initTestimonialsSwiperDesktop (carousel) {
    const carouselWrapper = carousel.querySelector(CAROUSEL_WRAPPER_SELECTOR);

    if( carouselWrapper != null ) {
        // Destroy the previous Swiper instance, if it exists
        destroySwiper(carousel.swiper);

        const sectionID = carousel.dataset.sectionId;
        const parentSelector = TESTIMONIALS_SELECTOR + '--' + sectionID;

        // Initialize the feature collection slider based on the unique classes generated above.
        const swiper = new Swiper(parentSelector + ' ' + CAROUSEL_SELECTOR, {
            // Optional parameters
            modules: [A11y, Keyboard, Autoplay],
            slidesPerView: SLIDES_PER_VIEW_TABLET,
            spaceBetween: 30,
            centerInsufficientSlides: true,
            observer: true,
            observeParents: true,
            a11y: false,
            watchOverflow: true, // Do not turn into sliders if the number of slides is less than slidesPerView.
            grabCursor: true,
            freeMode: true,
            loop: true,
            autoplay: {
                delay: 0,
                pauseOnMouseEnter: false,
                disableOnInteraction: false,
            },
            speed: 10000,
            breakpoints: {
                1024: {
                    slidesPerView: SLIDES_PER_VIEW_LAPTOP,
                },
                1366: {
                    slidesPerView: SLIDES_PER_VIEW_DESKTOP,
                },
            },
        });

        if (swiper.slides) {
            handleTestimonialSlides(swiper);
        } else {
            swiper.forEach(function(swiperEl) {
                handleTestimonialSlides(swiperEl);
            })
        }

        // Save the created Swiper instance to the DOM element
        carousel.swiper = swiper;

        // Check if the swiper instance exists before trying to stop autoplay
        if (carousel.swiper) {
            carousel.swiper.on('touchMove', function (e) {
                // Check if autoplay is enabled before stopping
                if (carousel.swiper.autoplay.running) {
                    carousel.swiper.autoplay.stop();
                }
                carousel.swiper.params.speed = 200;
            });
        }
    }
}

const initSwiperBreakpoint = () => {
    const tabletMedia = window.matchMedia(TABLET_MEDIA_QUERY);
    const testimonialsCarousel = document.querySelectorAll(TESTIMONIALS_SELECTOR);

    if(testimonialsCarousel != null) {
        if (tabletMedia.matches) {
            testimonialsCarousel.forEach(function (carousel) {
                initTestimonialsSwiperDesktop(carousel);
            });
        } else {
            testimonialsCarousel.forEach(function (carousel) {
                initTestimonialsSwiperMobile(carousel);
            });
        }
    }
}

// Function to destroy a Swiper instance
function destroySwiper(swiper) {
    if (swiper) {
        swiper.destroy(true, true);
    }
}

const addClassToElements = (elems, className) => {
    elems.forEach((elem) => {
        elem.classList.add(className);
    });
};


// -------------------------------------
// Hooks
// -------------------------------------
document.addEventListener('DOMContentLoaded', () => {
    initSwiperBreakpoint();
});

let resizeTimeout;
window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
        initSwiperBreakpoint();
    }, 500);
});
